import React, { useEffect, useRef, useState } from 'react'
import {
  CircularProgress,
  ConfirmationDialog,
  makeStyles,
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Typography,
  Grid,
  InputField,
  useIsMobile,
  SearchIcon,
  useDebouncedCallback
} from '@paintscout/ui'
import type { MuiTheme as Theme, DialogProps, StyleClasses } from '@paintscout/ui'
import { useClientOptions, useDialogs, useUserPermissions } from '@paintscout/ui-business'
import * as Sentry from '@sentry/react'
import { useSnackbar } from 'notistack'
import ClearIcon from '@material-ui/icons/Clear'
import { useSearchQuotesLazyQuery } from '@paintscout/api'
import type { Deal, Quote, SearchQuotesQuery } from '@paintscout/api'
import { getObjectLabels } from '@paintscout/util/builder'
import { jsonToLucene } from 'json-to-lucene'
import type { QuoteJobAddress } from 'paintscout'
import { useQuoteMutations } from 'shared/hooks'
import AddIcon from '@material-ui/icons/Add'
import QuoteResultsList from './QuoteResultsList'

const useStyles = makeStyles<Theme, LinkQuoteDialogProps>(
  (theme) => ({
    root: {},
    dialogTitle: {},
    dialogContent: {},
    dialogPaper: {
      overflow: 'visible',
      minWidth: 740,
      [theme.breakpoints.down('xs')]: {
        minWidth: 'unset'
      }
    },
    closeIcon: {
      cursor: 'pointer',
      position: 'absolute',
      right: theme.spacing(2),
      top: theme.spacing(2)
    },
    cancel: {},
    confirm: {},
    searchAdornment: {
      width: 24,
      height: 24,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    results: {
      top: -6,
      overflowY: 'auto',
      position: 'absolute',
      backgroundColor: theme.palette.background.paper,
      zIndex: 21474899999,
      width: 'calc(100% - 16px)',
      boxShadow: theme.shadows[1]
    },
    noResults: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: theme.spacing(2, 0),
      ...theme.typography.overline,
      color: theme.paintscout.palette.brand.Neutral.P80
    },
    addContactButton: {
      marginTop: 20, // needs to be top margin with an alignItems: 'flex-start' parent to keep alignment when validation error is showing
      [theme.breakpoints.down('sm')]: {
        marginBottom: theme.spacing(0),
        height: '37.75px'
      }
    }
  }),
  { name: 'LinkQuoteDialog' }
)

export interface LinkQuoteDialogProps extends DialogProps {
  classes?: DialogProps['classes'] & StyleClasses<typeof useStyles>
  invoice: boolean
  forDeal: Deal
  onConfirm: (quote: Partial<Quote>) => void
  onAddNew: () => void
  onCancel: (event: React.MouseEvent<HTMLElement>) => any
  onAlreadyLinked?: () => void
}

function LinkQuoteDialog(props: LinkQuoteDialogProps) {
  const classes = useStyles(props)
  const { onConfirm, onCancel, forDeal, onAddNew, invoice, onAlreadyLinked, ...baseDialogProps } = props
  const { permissionToCreateQuote } = useUserPermissions()
  const { linkDeal } = useQuoteMutations()
  const { enqueueSnackbar } = useSnackbar()
  const { options } = useClientOptions()
  const { dismissDialog, openDialog } = useDialogs()
  const objectLabels = getObjectLabels({ options })
  const pluralDocLabel = invoice ? 'Invoices' : objectLabels.quote.plural
  const docLabel = invoice ? 'Invoice' : objectLabels.quote.value
  const [isLoading, setIsLoading] = useState(false)
  const [query, setQuery] = useState('')
  const bookmark = useRef<string>()
  const sort = '-updated'

  const isMobile = useIsMobile({ xs: true })

  const [search, { loading, data, error }] = useSearchQuotesLazyQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    onCompleted: (d: SearchQuotesQuery) => {
      const newBookmark = d?.searchQuotes?.bookmark

      if (newBookmark && newBookmark !== bookmark.current) {
        bookmark.current = newBookmark
      }
    }
  })

  // Query and fetchMore debounced
  const debouncedQuery = useDebouncedCallback(search, 250)
  const hasQuery = !!query.trim()

  // On text change re-query
  useEffect(() => {
    if (hasQuery && debouncedQuery) {
      debouncedQuery({
        variables: {
          query: jsonToLucene({
            query,
            filters: [
              {
                id: 'trashed',
                type: 'equal',
                not: true,
                value: true
              },
              {
                id: 'is_invoice',
                type: 'equal',
                not: !invoice,
                value: true
              },
              forDeal &&
                forDeal?.contact?.id && {
                  type: 'and',
                  not: true,
                  value: [
                    {
                      id: 'contact.id',
                      type: 'equal',
                      not: true,
                      value: forDeal.contact.id
                    },
                    {
                      id: 'contact.id',
                      type: 'exists'
                    }
                  ]
                }
            ]
          }),
          sort
        }
      })
    }
  }, [debouncedQuery, query])

  useEffect(() => {
    if (error) {
      console.error(error)
      enqueueSnackbar(`Unable to search ${pluralDocLabel}`, { variant: 'error' })
    }
  }, [error])

  const quotes = data?.searchQuotes?.quotes ?? []
  const numResults = data?.searchQuotes?.total_rows ?? 0

  const showNoDocFound = hasQuery && numResults === 0 && !loading && data

  const rightContent = (
    <div onClick={onCancel} className={classes.closeIcon}>
      <ClearIcon />
    </div>
  )

  const onSelectQuote = async (quote: Partial<Quote>) => {
    if (hasAddressMismatch(quote.jobAddress, forDeal.jobAddress)) {
      openDialog(ConfirmationDialog, {
        title: 'Address Mismatch',
        message: `The job address on this ${docLabel.toLowerCase()} doesn't match the address listed on the deal. Do you want to update the ${docLabel.toLowerCase()} to use the deal's address?`,
        yesLabel: 'Yes, Update',
        noLabel: 'No Thanks',
        onConfirm: () => {
          dismissDialog()
          handleSubmit(quote, true)
        },
        onNoLabelClick: () => {
          dismissDialog()
          handleSubmit(quote, false)
        }
      })
    } else {
      handleSubmit(quote, false)
    }
  }

  const NoDocFoundText = (
    <Typography variant={'caption'} color={'textSecondary'}>
      No {pluralDocLabel.toLowerCase()} found for this contact. {pluralDocLabel} must be linked to the contact on this
      deal, or be unassigned.
    </Typography>
  )

  return (
    <Dialog {...baseDialogProps} onClose={onCancel} maxWidth="md" classes={{ paper: classes.dialogPaper }}>
      <DialogTitle classes={{ root: classes.dialogTitle }} rightContent={rightContent}>
        <Typography variant="h2">Add {docLabel}</Typography>
      </DialogTitle>
      <DialogContent className={classes.dialogPaper}>
        <Grid container spacing={2}>
          <Grid item xs>
            <InputField
              id="query"
              label={''}
              autoFocus
              fullWidth
              required
              error={!hasQuery}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search existing ${pluralDocLabel.toLowerCase()}`}
              autoComplete={'off'}
              startAdornment={
                <div className={classes.searchAdornment}>
                  {loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                </div>
              }
              endAdornment={
                hasQuery && numResults > 0 ? (
                  <Typography variant="subtitle1">
                    {numResults} result{numResults > 1 ? 's' : ''}
                  </Typography>
                ) : null
              }
            />
          </Grid>
          {permissionToCreateQuote && (
            <Grid item xs={'auto'}>
              <Button
                className="tour-link-quote-new-button"
                variant="dialog"
                color="primary"
                icon={AddIcon}
                onClick={onAddNew}
                disabled={loading || isLoading}
                size="medium"
              >
                New
              </Button>
            </Grid>
          )}

          {hasQuery && quotes.length > 0 && (
            <Grid item xs={12} style={{ position: 'relative' }}>
              <div className={classes.results}>
                <QuoteResultsList
                  results={quotes}
                  disabled={loading || isLoading}
                  onQuoteClick={onSelectQuote}
                  disabledRow={(quote: Partial<Quote>) => {
                    return quote.deal_id ? 'Already associated to deal' : undefined
                  }}
                />
                {data && !loading && quotes.length === 0 && <div className={classes.noResults}>No results</div>}
              </div>
            </Grid>
          )}
          {showNoDocFound && isMobile && NoDocFoundText}
        </Grid>
      </DialogContent>
      <DialogActions centeredButton>{showNoDocFound && !isMobile && NoDocFoundText}</DialogActions>
    </Dialog>
  )

  async function handleSubmit(quote: Partial<Quote>, updateJobAddress: boolean) {
    setIsLoading(true)
    try {
      await linkDeal(quote._id, forDeal._id, updateJobAddress ? true : undefined)
      onConfirm(quote)
    } catch (error) {
      const code = error?.graphQLErrors?.[0]?.extensions?.code
      if (code === 'ALREADY_LINKED_TO_DEAL') {
        enqueueSnackbar(
          `This deal already has ${/^[aeiou]/i.test(docLabel) ? 'an' : 'a'} ${docLabel.toLowerCase()} linked. Refreshing now.`,
          {
            variant: 'warning'
          }
        )
        // Don't call onConfirm(quote) here — it would optimistically add the
        // rejected quote and keep it visible until the next page load.
        if (onAlreadyLinked) {
          onAlreadyLinked()
        } else {
          dismissDialog()
        }
      } else {
        Sentry.captureException(error)
        console.log({ error })
        console.log(error)
        console.log('quote._id: ', quote?._id)
        console.log('forDeal._id: ', forDeal?._id)
        enqueueSnackbar('Something went wrong, please try again later', { variant: 'error' })
        dismissDialog()
      }
    }
    setIsLoading(false)
  }
}

export function hasAddressMismatch(
  quoteAddress?: QuoteJobAddress | null,
  dealAddress?: QuoteJobAddress | null
): boolean {
  // If the deal has no address, nothing to mismatch against
  if (!dealAddress?.address && !dealAddress?.city && !dealAddress?.province && !dealAddress?.postal) {
    return false
  }

  // If the quote has no address yet, nothing to mismatch from
  if (!quoteAddress?.address && !quoteAddress?.city && !quoteAddress?.province && !quoteAddress?.postal) {
    return false
  }

  const normalize = (val?: string | null) => (val || '').trim().toLowerCase()

  return (
    normalize(quoteAddress?.address) !== normalize(dealAddress?.address) ||
    normalize(quoteAddress?.city) !== normalize(dealAddress?.city) ||
    normalize(quoteAddress?.province) !== normalize(dealAddress?.province) ||
    normalize(quoteAddress?.postal) !== normalize(dealAddress?.postal)
  )
}

export default LinkQuoteDialog
